import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import client from "braintree-web/client";
import hostedFields from "braintree-web/hosted-fields";
import dataCollector from "braintree-web/data-collector";
import debounce from "lodash.debounce";
import { api } from "../services/api";
import { usePayPal, useThreeDSecure } from "../hooks";
import OrderSummary from "../components/OrderSummary";
import AddressAutocomplete from "../components/AddressAutocomplete";
import CheckoutSkeleton from "../components/CheckoutSkeleton";
import ShippingMethodSkeleton from "../components/ShippingMethodSkeleton";
import {
  initializeTracking,
  initializeFacebookPixelWithAM,
  trackCheckoutStarted,
  trackAddPaymentInfo,
  getTrackingParamsFromURL,
  getFacebookCookies
} from "../services/tracking";
import { calculateCartTotalFormatted } from "../utils/cartCalculations";
import { addressToFormFields, formFieldsToAddress, normalizePayPalAddress } from "../utils/addressNormalizer";
import logger from "../utils/logger";
import PhoneInput, { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import "./Checkout.css";

// Generar labels con código de país (+1, +51, etc.) - fuera del componente para performance
const countryLabelsWithCodes = Object.fromEntries(
  getCountries().map(country => [
    country,
    `${en[country]} (+${getCountryCallingCode(country)})`
  ])
);

const Checkout = () => {
  const { cartId: pathCartId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Support both path param (/checkout/:cartId) and query param (?cart_id=xxx)
  const cartId = pathCartId || searchParams.get("cart_id");

  // Google Places Autocomplete now using AutocompleteService (Programmatic API)
  // Benefits: Not deprecated, full styling control, same pricing as Legacy (~$0.051/session)

  const [cart, setCart] = useState(null);
  const [config, setConfig] = useState(null);
  const [hostedFieldsInstance, setHostedFieldsInstance] = useState(null);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  // Progressive loading states
  const [cartLoading, setCartLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [braintreeLoading, setBraintreeLoading] = useState(true);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'paypal'

  // PayPal Express mode (when coming from landing page PayPal button)
  const [isPayPalExpressMode, setIsPayPalExpressMode] = useState(false);

  // PayPal integration via custom hook
  const { paypalReady, paypalInstance, renderPayPalButton, cleanupPayPalButton } = usePayPal({ braintreeLoading });

  // 3D Secure integration via custom hook
  // Use null while config loads to avoid race condition (hook waits for actual mode)
  const threeDSMode = config
    ? (config.three_d_secure_mode || 'disabled')
    : null;
  const { threeDSecureReady, verifyCard } = useThreeDSecure({
    braintreeLoading,
    mode: threeDSMode,
  });

  // Refs to prevent duplicate initialization in StrictMode
  const isInitializing = useRef(false);
  const hasInitialized = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    emailOffers: true, // Pre-selected
    phone: "", // Teléfono contacto - react-phone-number-input devuelve E.164
    mobilePhone: "", // Teléfono SMS marketing (auto-fill del phone)
    textOffers: false, // Not pre-selected
    country: "United States",
    firstName: "",
    lastName: "",
    shippingAddress: "",
    shippingApt: "",
    shippingCity: "",
    shippingState: "",
    shippingZip: "",
    billingFirstName: "",
    billingLastName: "",
    billingAddress: "",
    billingApt: "",
    billingCity: "",
    billingState: "",
    billingZip: "",
    billingCountry: "United States",  // Default igual que shipping
  });

  // Billing same as shipping checkbox
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);

  // Phone validation errors (Shopify-style)
  const [phoneError, setPhoneError] = useState(false);
  const [mobilePhoneError, setMobilePhoneError] = useState(false);

  // Shipping method state
  const [shippingMethod, setShippingMethod] = useState(null);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [loadingShippingMethods, setLoadingShippingMethods] = useState(false);

  // Track if shipping methods need to be reloaded (trigger on blur instead of onChange)
  const [shouldReloadShipping, setShouldReloadShipping] = useState(false);

  // Countries and provinces from Shopify
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [billingProvinces, setBillingProvinces] = useState([]);  // Provincias para billing address

  // Ref to track initial mount (prevents clearing state on cart recovery)
  const isInitialMount = useRef(true);

  // 🔥 FIX: Ref to track if cart recovery orchestration is complete
  const cartRecoveryComplete = useRef(false);

  // 🔥 FIX: State to disable Google Places during cart recovery (prevent dropdown flash)
  // Starts false, will be enabled either:
  // 1. After cart recovery completes (if cart has saved address)
  // 2. Immediately when cart loads (if cart has NO saved address - fresh checkout)
  const [googlePlacesEnabled, setGooglePlacesEnabled] = useState(false);

  // 🔥 FIX: Enable Google Places immediately for fresh checkouts (no saved address)
  useEffect(() => {
    if (cart && !cart.shipping_address_json) {
      setGooglePlacesEnabled(true);
    }
  }, [cart]);

  // 💳 PayPal Express Mode: Detect when coming from landing page PayPal button
  // URL will have ?from=paypal_express parameter
  useEffect(() => {
    const fromParam = searchParams.get('from');
    if (fromParam === 'paypal_express') {
      setIsPayPalExpressMode(true);
      setPaymentMethod('paypal'); // Auto-select PayPal as payment method
      logger.debug('🚀 PayPal Express Mode activated - hiding Express Checkout, auto-selecting PayPal');
    }
  }, [searchParams]);

  // 🔥 FIX: Cart Recovery Orchestrator - Auto-trigger shipping methods after state restoration
  // This useEffect watches for shippingState changes and triggers shipping methods load
  // ONLY during initial cart recovery when all address fields are complete
  useEffect(() => {
    // Only run during initial cart recovery (not on subsequent user edits)
    if (isInitialMount.current && !cartRecoveryComplete.current) {
      // Check if ALL address fields are now complete (after provinces loaded + state restored)
      const isAddressComplete =
        formData.shippingCity &&
        formData.shippingState &&
        formData.shippingZip &&
        formData.shippingZip.trim().length >= 5;

      if (isAddressComplete) {
        // Mark cart recovery as complete (prevent future triggers)
        cartRecoveryComplete.current = true;

        // Mark initial mount as complete
        isInitialMount.current = false;

        // Trigger shipping methods load
        setShouldReloadShipping(true);

        // 🔥 FIX: Google Places will be enabled when user interacts with Address field
        // This prevents dropdown flash during cart recovery pre-fill
      }
    }
  }, [formData.shippingCity, formData.shippingState, formData.shippingZip]);

  // Tip state
  const [tipEnabled, setTipEnabled] = useState(true); // Pre-checked
  const [selectedTipOption, setSelectedTipOption] = useState("none"); // '5', '10', '20', 'custom', 'none'
  const [customTipAmount, setCustomTipAmount] = useState("");
  const [appliedTip, setAppliedTip] = useState(0);
  const [showTipThankYou, setShowTipThankYou] = useState(false);
  const [lastAppliedCustomTip, setLastAppliedCustomTip] = useState(""); // Track last applied custom tip
  const [tipError, setTipError] = useState(""); // Error message for tip validation
  const [tipProcessing, setTipProcessing] = useState(false); // Loading state for tip application

  // Google Places states
  const [addressSource, setAddressSource] = useState("manual"); // 'google' | 'manual'
  const [googlePlaceSelected, setGooglePlaceSelected] = useState(false);
  const [googlePlacesAvailable, setGooglePlacesAvailable] = useState(false);

  // Loading states
  const [isLoadingCart, setIsLoadingCart] = useState(true);

  // Card validation state
  const [cardValidation, setCardValidation] = useState({
    number: false,
    expirationDate: false,
    cvv: false,
    cardholderName: false,
  });

  // Card field error messages
  const [cardFieldErrors, setCardFieldErrors] = useState({
    number: '',
    expirationDate: '',
    cvv: '',
    cardholderName: '',
  });

  // Function to refresh cart data (used by OrderSummary after discount changes)
  const refreshCart = async () => {
    try {
      const cartData = await api.getCart(cartId);
      setCart(cartData);
    } catch (err) {
      console.error("Error refreshing cart:", err);
    }
  };

  // Progressive loading: Load everything in parallel
  useEffect(() => {
    // Prevent duplicate initialization in StrictMode
    if (isInitializing.current || hasInitialized.current) {
      return;
    }

    const initCheckout = async () => {
      isInitializing.current = true;

      try {
        // STEP 0: Ultra-fast redirect check (before loading anything else)
        // This is the MOST PROFESSIONAL solution - check status via lightweight endpoint
        const redirectCheck = await api.checkCartRedirect(cartId);

        if (redirectCheck.redirect) {
          // Navigate immediately without loading cart/config/braintree
          navigate(redirectCheck.redirect_url);
          return; // Stop all execution
        }

        // If we get here, cart is valid (pending) - proceed with normal loading
        // 1. Load cart data FIRST to check status before rendering
        const loadCart = async () => {
          setIsLoadingCart(true); // Start loading state

          try {
            const cartData = await api.getCart(cartId);

            // Double-check status (belt and suspenders approach)
            if (cartData.status === "completed") {
              if (cartData.order_id) {
                // Build redirect URL with GA4 cross-domain parameter if present
                const redirectParams = new URLSearchParams({ order_id: cartData.order_id });
                const glParam = searchParams.get('_gl');
                if (glParam) redirectParams.set('_gl', glParam);
                navigate(`/thank-you?${redirectParams.toString()}`);
              } else {
                setError("This order has already been completed.");
                setLoading(false);
              }
              setIsLoadingCart(false);
              return;
            }

            // Cart recovery: Allow abandoned carts to be completed
            // Users can return via email recovery links and complete their purchase
            // Status 'abandoned' is treated the same as 'pending' for checkout purposes
            if (cartData.status === "abandoned") {
              logger.debug(
                "✅ Cart recovery: Allowing abandoned cart to be completed"
              );
              // Continue with normal checkout flow - don't block
            }

            if (cartData.status === "processing") {
              setError(
                "This cart is currently being processed. Please wait or refresh the page."
              );
              setLoading(false);
              setIsLoadingCart(false);
              return;
            }

            setCart(cartData);

            // Pre-fill form from saved cart data (cart recovery)
            // Pass shipping_countries for country code to name conversion
            prefillFormFromCart(cartData, cartData.store?.shipping_countries || []);

            // DON'T load shipping methods from cart - they're not filtered by address yet
            // Shipping methods will be loaded only after user enters full shipping address
            // This matches Shopify native checkout behavior

            // Apply branding from cart.store (no need for separate config call)
            if (cartData.store) {
              // Apply CSS color variables
              if (cartData.store.primary_color) {
                document.documentElement.style.setProperty(
                  "--primary-color",
                  cartData.store.primary_color
                );
              }
              if (cartData.store.secondary_color) {
                document.documentElement.style.setProperty(
                  "--secondary-color",
                  cartData.store.secondary_color
                );
              }

              // Load GTM if configured
              if (cartData.store.gtm_container_id) {
                loadGTM(cartData.store.gtm_container_id);
              }

              // Store config in state
              // ⭐ ARCHITECTURAL FIX: Spread all store fields first, then add nested objects
              // This ensures new fields (like three_d_secure_mode) are automatically included
              setConfig({
                ...cartData.store, // Include ALL store fields automatically
                branding: {
                  logo_url: cartData.store.logo_url,
                  logo_max_height: cartData.store.logo_max_height,
                  logo_alignment: cartData.store.logo_alignment,
                  header_background_color:
                    cartData.store.header_background_color,
                  primary_color: cartData.store.primary_color,
                  secondary_color: cartData.store.secondary_color,
                },
                tracking: {
                  gtm_container_id: cartData.store.gtm_container_id,
                  facebook_pixel_id: cartData.store.facebook_pixel_id,
                  google_ads_id: cartData.store.google_ads_id,
                  google_ads_conversion_label:
                    cartData.store.google_ads_conversion_label,
                  google_enhanced_conversions_enabled:
                    cartData.store.google_enhanced_conversions_enabled,
                  tiktok_pixel_id: cartData.store.tiktok_pixel_id,
                  snapchat_pixel_id: cartData.store.snapchat_pixel_id,
                  twitter_pixel_id: cartData.store.twitter_pixel_id,
                  pinterest_tag_id: cartData.store.pinterest_tag_id,
                },
              });

              // Initialize tracking pixels (FB, Google, GA4, TikTok, etc.)
              const trackingConfig = {
                gtm_container_id: cartData.store.gtm_container_id,
                facebook_pixel_id: cartData.store.facebook_pixel_id,
                google_analytics_id: cartData.store.google_analytics_id,
                google_ads_id: cartData.store.google_ads_id,
                google_ads_conversion_label: cartData.store.google_ads_conversion_label,
                tiktok_pixel_id: cartData.store.tiktok_pixel_id,
                snapchat_pixel_id: cartData.store.snapchat_pixel_id,
                twitter_pixel_id: cartData.store.twitter_pixel_id,
                pinterest_tag_id: cartData.store.pinterest_tag_id,
              };

              // Initialize all tracking pixels
              initializeTracking(
                trackingConfig,
                cartData.store.custom_head_script,
                cartData.store.custom_body_script
              );

              // Fire InitiateCheckout event
              const checkoutEventId = trackCheckoutStarted(trackingConfig, {
                cart_id: cartData.cart_id,
                subtotal: cartData.subtotal,
                items: cartData.items.map(item => ({
                  shopify_id: item.shopify_id || item.product?.shopify_id,
                  product_id: item.product_id,
                  name: item.product_name || item.name,
                  price: item.price_at_add || item.price,
                  quantity: item.quantity
                }))
              });
              logger.debug('📊 InitiateCheckout event fired with eventId:', checkoutEventId);

              // Store tracking params and event IDs for later use in checkout
              const urlTrackingParams = getTrackingParamsFromURL();
              const fbCookies = getFacebookCookies();
              sessionStorage.setItem('checkout_tracking', JSON.stringify({
                ...urlTrackingParams,
                ...fbCookies,
                initiate_checkout_event_id: checkoutEventId
              }));

              // Load countries from Shopify shipping zones
              logger.debug(
                "🌍 Shipping countries from API:",
                cartData.store.shipping_countries
              );
              if (
                cartData.store.shipping_countries &&
                cartData.store.shipping_countries.length > 0
              ) {
                logger.debug(
                  "✅ Setting countries:",
                  cartData.store.shipping_countries.length
                );
                setCountries(cartData.store.shipping_countries);
                // Set default country to first in list
                if (!formData.country) {
                  setFormData((prev) => ({
                    ...prev,
                    country: cartData.store.shipping_countries[0].name,
                  }));
                }
              } else {
              }

              setConfigLoading(false);
            }

            setCartLoading(false);
          } catch (err) {
            console.error("Error loading cart:", err);
            setError(err.message || "Failed to load cart.");
            setCartLoading(false);
            setIsLoadingCart(false);
          } finally {
            setIsLoadingCart(false);
          }
        };

        // 2. Load checkout configuration - NOW INCLUDED IN CART RESPONSE
        // No need for separate /api/config call - cart.store contains all branding
        const loadConfig = async () => {
          // Config is now loaded from cart.store in loadCart()
          // This function is kept for compatibility but does nothing
          // setConfigLoading is now set to false in loadCart() after applying cart.store branding
        };

        // 3. Initialize Braintree (parallel, slowest operation)
        const loadBraintree = async () => {
          try {
            const clientToken = await api.getBraintreeToken();
            const clientInstance = await client.create({
              authorization: clientToken,
            });

            // Store client instance for later use
            window.braintreeClient = clientInstance;
            setBraintreeLoading(false);

          } catch (err) {
            console.error("Error initializing Braintree:", err);
            setError(
              "Failed to initialize payment system. Please refresh the page."
            );
            setBraintreeLoading(false);
          }
        };

        // STEP 1: Load cart FIRST to check status (avoid flash on completed carts)
        await loadCart();

        // If we're still here, cart is valid - now load everything else in parallel
        await Promise.all([loadConfig(), loadBraintree()]);

        // Show the form only after verifying cart is valid
        setLoading(false);

        // Fire GTM checkout_loaded event after cart is loaded
        if (window.dataLayer && cart) {
          window.dataLayer.push({
            event: "checkout_loaded",
            ecommerce: {
              checkout: {
                actionField: { step: 1 },
                products: cart.items.map((item) => ({
                  name: item.product.name,
                  id: item.product.clickfunnels_id,
                  price: item.price,
                  variant: item.variant_id || "",
                  quantity: item.quantity,
                })),
              },
            },
          });
        }

        // Mark as successfully initialized
        hasInitialized.current = true;
      } catch (err) {
        console.error("Error initializing checkout:", err);
        setError(err.message || "Failed to load checkout. Please try again.");
        setLoading(false);
      } finally {
        isInitializing.current = false;
      }
    };

    if (cartId) {
      initCheckout();
    }

    // Cleanup
    return () => {
      if (hostedFieldsInstance) {
        hostedFieldsInstance.teardown();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  // US State name to code mapping
  const STATE_CODES = {
    Alabama: "AL",
    Alaska: "AK",
    Arizona: "AZ",
    Arkansas: "AR",
    California: "CA",
    Colorado: "CO",
    Connecticut: "CT",
    Delaware: "DE",
    Florida: "FL",
    Georgia: "GA",
    Hawaii: "HI",
    Idaho: "ID",
    Illinois: "IL",
    Indiana: "IN",
    Iowa: "IA",
    Kansas: "KS",
    Kentucky: "KY",
    Louisiana: "LA",
    Maine: "ME",
    Maryland: "MD",
    Massachusetts: "MA",
    Michigan: "MI",
    Minnesota: "MN",
    Mississippi: "MS",
    Missouri: "MO",
    Montana: "MT",
    Nebraska: "NE",
    Nevada: "NV",
    "New Hampshire": "NH",
    "New Jersey": "NJ",
    "New Mexico": "NM",
    "New York": "NY",
    "North Carolina": "NC",
    "North Dakota": "ND",
    Ohio: "OH",
    Oklahoma: "OK",
    Oregon: "OR",
    Pennsylvania: "PA",
    "Rhode Island": "RI",
    "South Carolina": "SC",
    "South Dakota": "SD",
    Tennessee: "TN",
    Texas: "TX",
    Utah: "UT",
    Vermont: "VT",
    Virginia: "VA",
    Washington: "WA",
    "West Virginia": "WV",
    Wisconsin: "WI",
    Wyoming: "WY",
  };

  // Fallback US states as array
  const US_STATES = Object.keys(STATE_CODES).map((name) => ({
    code: STATE_CODES[name],
    name: name,
  }));

  // ⭐ Helper Functions - Centralized conversion logic
  const getCountryCode = (countryName) => {
    // First try to get from Shopify countries (loaded from API)
    const country = countries.find(c => c.name === countryName);
    if (country?.code) return country.code;

    // Fallback: Map common country names to ISO codes
    const countryMap = {
      "United States": "US",
      Canada: "CA",
      Mexico: "MX",
      "United Kingdom": "GB",
    };
    return countryMap[countryName] || 'US';
  };

  const getStateCode = (stateName) => {
    // Try STATE_CODES first (US states), then provinces from API
    if (STATE_CODES[stateName]) {
      return STATE_CODES[stateName];
    }
    // Check if it's in provinces list
    const province = provinces.find((p) => p.name === stateName);
    return province ? province.code : stateName;
  };

  // Get state code for billing address (uses billingProvinces)
  const getBillingStateCode = (stateName) => {
    // Buscar en billingProvinces primero
    const province = billingProvinces.find((p) => p.name === stateName);
    if (province) return province.code;

    // Fallback a STATE_CODES (US states)
    if (STATE_CODES[stateName]) {
      return STATE_CODES[stateName];
    }

    return stateName;
  };

  const getValidPhone = (phone) => {
    // Phone must have more than country code (+1)
    const isValid = phone && phone.trim().length > 3;
    logger.debug('📱 [PHONE] getValidPhone:', { phone, isValid });
    return isValid ? phone.trim() : null;
  };

  // Reload shipping methods when user finishes entering address (onBlur instead of onChange)
  useEffect(() => {
    const reloadShippingMethods = async () => {
      // Only reload if trigger is set and we have required address fields
      if (!shouldReloadShipping) {
        return;
      }

      if (
        !formData.shippingCity ||
        !formData.shippingState ||
        !formData.shippingZip ||
        !cartId
      ) {
        setShouldReloadShipping(false);
        return;
      }

      // Skip if cart is not loaded yet
      if (!cart) {
        setShouldReloadShipping(false);
        return;
      }

      try {
        setLoadingShippingMethods(true);

        // Use helper functions for conversion
        const countryCode = getCountryCode(formData.country);
        const stateCode = getStateCode(formData.shippingState);

        // Call new Shopify Storefront API endpoint
        const result = await api.getShippingMethods(cartId, {
          address1: formData.shippingAddress,
          address2: formData.shippingApt,
          city: formData.shippingCity,
          provinceCode: stateCode,
          zip: formData.shippingZip,
          countryCode: countryCode,
          firstName: formData.firstName,
          lastName: formData.lastName,
        });

        // Update shipping methods (these are already filtered by Shopify)
        if (result.shipping_methods && result.shipping_methods.length > 0) {
          setShippingMethods(result.shipping_methods);
          // Auto-select first method (use handle from Shopify)
          setShippingMethod(result.shipping_methods[0].handle);
        } else {
          // No shipping methods available for this location
          setShippingMethods([]);
          setShippingMethod(null);
        }
      } catch (err) {
        // Don't show error to user, just keep current methods
        console.error("Error fetching shipping methods:", err);
      } finally {
        setLoadingShippingMethods(false);
        setShouldReloadShipping(false); // Reset trigger
      }
    };

    reloadShippingMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReloadShipping]);

  // Safety net: Save form data before tab/window closes (v309)
  // This ensures data is saved even if user closes tab without triggering blur
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Save all form data immediately before tab closes
      saveFormDataIncremental();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, cartId]);

  // Load provinces when country changes
  // Track previous country to detect actual country changes
  const prevLoadedCountryRef = useRef(null);

  useEffect(() => {
    const loadProvinces = async () => {
      if (
        !config ||
        !countries ||
        countries.length === 0 ||
        !cart?.store?.domain
      ) {
        return;
      }

      // Find selected country
      const selectedCountry = countries.find(
        (c) => c.name === formData.country
      );
      if (!selectedCountry) {
        return;
      }

      // If country doesn't have provinces, clear the list
      if (!selectedCountry.has_provinces) {
        setProvinces([]);
        return;
      }

      // Check if country actually changed (not just cart or other deps)
      const countryActuallyChanged = prevLoadedCountryRef.current !== null &&
                                      prevLoadedCountryRef.current !== formData.country;

      try {
        // Fetch provinces from API using cart's store domain
        const data = await api.getProvinces(
          cart.store.domain,
          selectedCountry.code
        );

        if (data.provinces) {
          setProvinces(data.provinces);

          // Restore states from cart if this is initial load (after provinces are loaded)
          if (isInitialMount.current) {
            const updates = {};
            if (cart?.shipping_address_json?.province) {
              updates.shippingState = cart.shipping_address_json.province;
            }
            if (cart?.billing_address_json?.province) {
              updates.billingState = cart.billing_address_json.province;
            }

            if (Object.keys(updates).length > 0) {
              setFormData((prev) => ({ ...prev, ...updates }));
            } else {
              // Mark initial mount as complete even if no updates
              isInitialMount.current = false;
            }
          } else if (countryActuallyChanged) {
            // Only clear states if the COUNTRY actually changed (not cart/config/etc)
            logger.debug('🔄 [loadProvinces] País cambió, reseteando estado');
            setFormData((prev) => ({
              ...prev,
              shippingState: "",
              billingState: "",
            }));
          }
          // If cart changed but country didn't, do nothing - keep current state
        }

        // Update the ref AFTER processing
        prevLoadedCountryRef.current = formData.country;

      } catch (err) {
        console.error("Error loading provinces:", err);
        setProvinces([]);
      }
    };

    loadProvinces();
  }, [formData.country, config, countries, cart]);

  // Load billing provinces when billing country changes
  useEffect(() => {
    const loadBillingProvinces = async () => {
      // Solo cargar si el checkbox está desmarcado
      if (billingSameAsShipping) {
        setBillingProvinces([]);
        return;
      }

      if (!countries || countries.length === 0 || !cart?.store?.domain) {
        return;
      }

      // Find selected billing country
      const selectedCountry = countries.find(
        (c) => c.name === formData.billingCountry
      );
      if (!selectedCountry) {
        return;
      }

      // If country doesn't have provinces, clear the list
      if (!selectedCountry.has_provinces) {
        setBillingProvinces([]);
        return;
      }

      try {
        const data = await api.getProvinces(
          cart.store.domain,
          selectedCountry.code
        );

        if (data.provinces) {
          setBillingProvinces(data.provinces);

          // Restaurar billingState si viene de cart recovery
          if (isInitialMount.current && cart?.billing_address_json?.province) {
            setFormData((prev) => ({
              ...prev,
              billingState: cart.billing_address_json.province
            }));
          }
        }
      } catch (err) {
        console.error("Error loading billing provinces:", err);
        setBillingProvinces([]);
      }
    };

    loadBillingProvinces();
  }, [formData.billingCountry, billingSameAsShipping, countries, cart]);

  // Initialize Braintree Hosted Fields after Braintree client is ready
  useEffect(() => {
    const initHostedFields = async () => {
      if (!window.braintreeClient || braintreeLoading || hostedFieldsInstance)
        return;

      try {
        // Wait a bit to ensure DOM is ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Verify all elements exist
        const selectors = [
          "#card-number",
          "#cvv",
          "#expiration-date",
          "#cardholder-name",
        ];
        const allExist = selectors.every((sel) => document.querySelector(sel));

        if (!allExist) {
          return;
        }

        // Create Hosted Fields instance
        const fieldsInstance = await hostedFields.create({
          client: window.braintreeClient,
          styles: {
            input: {
              "font-size": "14px",
              "font-family":
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              color: "#333",
            },
            "input.invalid": {
              color: "#d9534f",
            },
            // Removed green color on valid fields - keep neutral like Shopify
            // "input.valid": {
            //   color: "#5cb85c",
            // },
            ":focus": {
              color: "#333",
            },
          },
          fields: {
            number: {
              selector: "#card-number",
              placeholder: "1234 1234 1234 1234",
            },
            cvv: {
              selector: "#cvv",
              placeholder: "123",
            },
            expirationDate: {
              selector: "#expiration-date",
              placeholder: "Expiration date (MM / YY)",
            },
            cardholderName: {
              selector: "#cardholder-name",
              placeholder: "John Doe",
            },
          },
        });

        // Listen for field events
        fieldsInstance.on("validityChange", (event) => {
          const field = event.fields[event.emittedBy];
          const fieldName = event.emittedBy;

          // Update validation state
          setCardValidation((prev) => ({
            ...prev,
            [fieldName]: field.isValid,
          }));

          // Set error messages for invalid fields
          if (!field.isValid && !field.isEmpty) {
            const errorMessages = {
              number: "Enter a valid card number",
              expirationDate: "Enter a valid expiration date (MM / YY)",
              cvv: "Enter a valid security code",
              cardholderName: "Enter the cardholder's name",
            };

            setCardFieldErrors((prev) => ({
              ...prev,
              [fieldName]: errorMessages[fieldName] || "",
            }));
          } else {
            // Clear error when valid or empty
            setCardFieldErrors((prev) => ({
              ...prev,
              [fieldName]: "",
            }));
          }
        });

        // Listen for empty events to clear errors when user starts typing
        fieldsInstance.on("empty", (event) => {
          const fieldName = event.emittedBy;
          setCardFieldErrors((prev) => ({
            ...prev,
            [fieldName]: "",
          }));
        });

        setHostedFieldsInstance(fieldsInstance);

        // Create Data Collector instance (for fraud detection)
        const dataCollectorInst = await dataCollector.create({
          client: window.braintreeClient,
          kount: true,
        });

        setDataCollectorInstance(dataCollectorInst);

      } catch (err) {
        console.error("Error initializing Hosted Fields:", err);
        setError(
          "Failed to initialize payment fields. Please refresh the page."
        );
      }
    };

    // Only initialize when payment method is 'card' and braintree is ready
    if (!braintreeLoading && paymentMethod === 'card') {
      initHostedFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [braintreeLoading, paymentMethod]);

  // Handle payment method changes - teardown/reinitialize Hosted Fields
  useEffect(() => {
    const handlePaymentMethodChange = async () => {
      if (paymentMethod === 'paypal' && hostedFieldsInstance) {
        // Teardown Hosted Fields when switching to PayPal
        try {
          await hostedFieldsInstance.teardown();
          setHostedFieldsInstance(null);
          logger.debug('🔄 Hosted Fields teardown - switching to PayPal');
        } catch (err) {
          console.error('Error tearing down Hosted Fields:', err);
        }
      }
    };

    handlePaymentMethodChange();
  }, [paymentMethod, hostedFieldsInstance]);

  /**
   * Handle Google Places autocomplete selection (Legacy API)
   * No need for input swap - Legacy API works directly with native input
   */
  const handlePlaceSelected = ({ parsedAddress, streetAddress }) => {
    logger.debug("🏠 Street address:", streetAddress);

    // Auto-fill form fields
    const updates = {
      shippingAddress: streetAddress || "",
    };

    // If no street address was found, warn user
    if (!streetAddress) {
      logger.warn(
        "⚠️ No street address found. Selected location may be too general (city/region)."
      );
    }

    if (parsedAddress.city) {
      updates.shippingCity = parsedAddress.city;
    }

    if (parsedAddress.state) {
      updates.shippingState = parsedAddress.state; // Use full state name for dropdown
      logger.debug("✅ Setting state to:", parsedAddress.state);
    } else {
      logger.warn("⚠️ No state found in address components");
    }

    if (parsedAddress.zip) {
      updates.shippingZip = parsedAddress.zip;
    }

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));

    logger.debug("📍 Form updates:", updates);

    // Mark as Google-filled
    setAddressSource("google");
    setGooglePlaceSelected(true);

    logger.debug("✅ Address auto-filled successfully");
  };

  // Load Google Tag Manager
  const loadGTM = (gtmId) => {
    if (window.gtmLoaded) return; // Already loaded

    const script = document.createElement("script");
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);
    window.gtmLoaded = true;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // If user manually edits after Google auto-fill, switch to manual mode
    if (addressSource === "google" && name.startsWith("shipping")) {
      setAddressSource("manual");
    }

    // Note: Email sync moved to handleEmailBlur to prevent premature saving
    // while user is still typing (e.g., "pellanne.christian@gmail.co" before finishing ".com")
  };

  // Handle email blur - sync with Shopify for abandoned cart tracking
  const handleEmailBlur = () => {
    const email = formData.email;

    // Only proceed if email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return;
    }

    // Sync email with Shopify for abandoned cart tracking
    const validPhone = getValidPhone(formData.phone);
    syncCustomerWithShopify(email, validPhone);
  };

  // Handle phone blur - save immediately when user leaves phone field
  // This ensures phone is saved even if user closes page before debounce completes
  const handlePhoneBlur = () => {
    const phone = formData.phone?.trim();

    // Validación estilo Shopify: mostrar error si el número es muy corto
    if (phone && phone.length > 0 && phone.length < 8) {
      setPhoneError(true);
    } else {
      setPhoneError(false);
    }

    if (phone && phone.length > 3) {
      // Save immediately when user finishes editing phone
      saveFormDataIncremental();
    }
  };

  // Handle mobile phone blur (validación estilo Shopify)
  const handleMobilePhoneBlur = () => {
    const mobilePhone = formData.mobilePhone?.trim();

    if (mobilePhone && mobilePhone.length > 0 && mobilePhone.length < 8) {
      setMobilePhoneError(true);
    } else {
      setMobilePhoneError(false);
    }
  };

  // Handle shipping address blur (trigger shipping methods reload AND sync with Shopify)
  const handleShippingAddressBlur = () => {
    // Skip if Google just filled (avoid duplicate search)
    if (googlePlaceSelected) {
      setGooglePlaceSelected(false);
      return;
    }

    // Save all form data incrementally
    saveFormDataIncremental();

    // Validate ZIP has minimum 5 digits
    const isValidZip =
      formData.shippingZip && formData.shippingZip.trim().length >= 5;

    // Only trigger shipping methods reload if all fields complete AND ZIP is valid
    if (formData.shippingCity && formData.shippingState && isValidZip) {
      setShouldReloadShipping(true);
    }
  };

  // Handle marketing preferences change (email/SMS checkboxes)
  const handleMarketingPreferenceChange = (e) => {
    const { name, checked } = e.target;

    // ✅ Compute new values BEFORE state update (avoid React timing issues)
    const newMarketingPrefs = {
      emailOffers: name === "emailOffers" ? checked : formData.emailOffers,
      textOffers: name === "textOffers" ? checked : formData.textOffers,
    };

    // Auto-fill mobilePhone con el valor de phone cuando se marca textOffers
    if (name === 'textOffers' && checked && formData.phone) {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        mobilePhone: formData.phone  // Auto-fill
      }));
    } else {
      // Update state (async)
      setFormData((prev) => ({ ...prev, [name]: checked }));
    }

    // ✅ Save immediately with actual new values (follows googlePlacesOverride pattern)
    saveFormDataIncremental(null, null, newMarketingPrefs);
  };

  // Sync shipping address with Shopify for draft order update
  const syncShippingAddressWithShopify = async () => {

    // Only sync if we have all required fields
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.shippingAddress ||
      !formData.shippingCity ||
      !formData.shippingState ||
      !formData.shippingZip
    ) {
      logger.warn("⚠️ [AUTO-SAVE] Missing required fields, skipping sync");
      return;
    }

    // Use helper functions for conversion
    const stateCode = getStateCode(formData.shippingState);
    const countryCode = getCountryCode(formData.country);

    const shippingAddress = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      address1: formData.shippingAddress,
      address2: formData.shippingApt || "",
      city: formData.shippingCity,
      province: formData.shippingState,
      province_code: stateCode,
      zip: formData.shippingZip,
      country: formData.country,
      country_code: countryCode,
    };

    // Use helper function for phone validation
    const validPhone = getValidPhone(formData.phone);

    // Build billing address if different from shipping
    const billingAddress = !billingSameAsShipping
      ? {
          first_name: formData.billingFirstName || formData.firstName,
          last_name: formData.billingLastName || formData.lastName,
          address1: formData.billingAddress,
          city: formData.billingCity,
          province: formData.billingState,
          province_code: getBillingStateCode(formData.billingState),
          zip: formData.billingZip,
          country: formData.billingCountry,
          country_code: getCountryCode(formData.billingCountry),
        }
      : null;

    const result = await api.syncCustomer(
      cartId,
      formData.email || null, // Email may not be filled yet
      validPhone,
      shippingAddress,
      {
        country: formData.country,
        accepts_email_marketing: formData.emailOffers,
        accepts_sms_marketing: formData.textOffers,
        billing_address: billingAddress,
      }
    );

    if (result) {
      logger.debug("✅ Shipping address synced with Shopify:", result);
    }
  };

  // Sync customer info with Shopify for abandoned cart tracking
  const syncCustomerWithShopify = async (email, phone) => {
    const result = await api.syncCustomer(cartId, email, phone, null, {
      country: formData.country,
      accepts_email_marketing: formData.emailOffers,
      accepts_sms_marketing: formData.textOffers,
    });
    if (result) {
      logger.debug(
        "✅ Customer synced with Shopify for abandoned cart tracking:",
        result
      );
    }
  };

  /**
   * Auto-save form data incrementally (NO strict validations)
   * Saves whatever data is available at the moment
   * Called on blur events to persist data for cart recovery
   *
   * @param {Object} googlePlacesOverride - Optional: parsed address from Google Places (professional approach)
   * @param {string} streetAddressOverride - Optional: street address from Google Places
   * @param {Object} marketingOverride - Optional: marketing preferences {emailOffers, textOffers} (for immediate checkbox sync)
   */
  const saveFormDataIncremental = useCallback(
    async (
      googlePlacesOverride = null,
      streetAddressOverride = null,
      marketingOverride = null // ✨ NEW: Marketing preferences override
    ) => {
      logger.debug("💾 [AUTO-SAVE] Saving form data incrementally...");

      // ✅ PROFESSIONAL: Use real-time Google Places data if provided
      const addressData = googlePlacesOverride
        ? {
            address: streetAddressOverride || formData.shippingAddress,
            city: googlePlacesOverride.city || formData.shippingCity,
            state: googlePlacesOverride.state || formData.shippingState,
            zip: googlePlacesOverride.zip || formData.shippingZip,
          }
        : {
            address: formData.shippingAddress,
            city: formData.shippingCity,
            state: formData.shippingState,
            zip: formData.shippingZip,
          };

      // ✅ Use marketing override if provided (for immediate checkbox sync)
      const emailOffers =
        marketingOverride?.emailOffers ?? formData.emailOffers;
      const textOffers = marketingOverride?.textOffers ?? formData.textOffers;

      const saveData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        ...addressData,
        country: formData.country,
        emailOffers,
        textOffers,
        source: googlePlacesOverride
          ? "Google Places (real-time)"
          : marketingOverride
          ? "Marketing override (real-time)"
          : "formData (state)",
      };

      // Build shipping address if any address fields are filled
      const hasAddressData =
        addressData.address ||
        addressData.city ||
        addressData.state ||
        addressData.zip;

      let shippingAddress = null;
      if (hasAddressData) {
        const stateCode = getStateCode(addressData.state);
        const countryCode = getCountryCode(formData.country);

        shippingAddress = {
          first_name: formData.firstName || "",
          last_name: formData.lastName || "",
          address1: addressData.address || "",
          address2: formData.shippingApt || "",
          city: addressData.city || "",
          province: addressData.state || "",
          province_code: stateCode || "",
          zip: addressData.zip || "",
          country: formData.country || "",
          country_code: countryCode || "",
        };
      }

      // Build billing address if different from shipping
      let billingAddress = null;
      if (!billingSameAsShipping && formData.billingAddress) {
        const billingStateCode = getBillingStateCode(formData.billingState);
        const billingCountryCode = getCountryCode(formData.billingCountry);

        billingAddress = {
          first_name: formData.billingFirstName || formData.firstName || "",
          last_name: formData.billingLastName || formData.lastName || "",
          address1: formData.billingAddress || "",
          address2: formData.billingApt || "",
          city: formData.billingCity || "",
          province: formData.billingState || "",
          province_code: billingStateCode || "",
          zip: formData.billingZip || "",
          country: formData.billingCountry || "",
          country_code: billingCountryCode || "",
        };
      }

      const validPhone = getValidPhone(formData.phone);
      logger.debug('📱 [PHONE] saveFormDataIncremental:', {
        'formData.phone': formData.phone,
        'validPhone': validPhone
      });

      try {
        const result = await api.syncCustomer(
          cartId,
          formData.email || null,
          validPhone,
          shippingAddress,
          {
            country: formData.country,
            accepts_email_marketing: emailOffers, // ✅ Use override value
            accepts_sms_marketing: textOffers, // ✅ Use override value
            billing_address: billingAddress,
            mobile_phone: formData.mobilePhone || null, // SMS marketing phone (not sent to Shopify)
          }
        );

        if (result && result.success) {
          logger.debug("✅ [AUTO-SAVE] Form data saved:", result.updated_fields);
        }
      } catch (error) {
        logger.warn("⚠️ [AUTO-SAVE] Failed to save form data:", error);
      }
    },
    [cartId, formData, billingSameAsShipping]
  );

  /**
   * Debounced auto-save for optional fields (phone, apartment)
   *
   * Use for fields that don't have special onBlur logic.
   * Auto-saves after user stops typing for 1.5 seconds.
   *
   * ✅ Use for: phone, shippingApt (optional fields)
   * ❌ Don't use for: email (has auto-fill), address fields (reload shipping)
   */
  const debouncedSave = useCallback(
    debounce(() => {
      logger.debug("💾 [DEBOUNCED-SAVE] Auto-saving optional field...");
      saveFormDataIncremental();
    }, 1500),
    [saveFormDataIncremental]
  );

  /**
   * Pre-fill form from saved cart data (cart recovery)
   * Validates data age and completeness
   * @param {Object} cartData - Cart data from API
   * @param {Array} availableCountries - List of countries [{name, code}, ...] for code-to-name conversion
   */
  const prefillFormFromCart = (cartData, availableCountries = []) => {
    if (!cartData) {
      return;
    }

    // Check cart age (warn if > 30 days old)
    const cartAge = Date.now() - new Date(cartData.created_at).getTime();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    const isStaleCart = cartAge > thirtyDays;

    const updates = {};

    // Pre-fill contact info (always safe)
    if (cartData.customer_email) {
      updates.email = cartData.customer_email;
    }
    if (cartData.customer_phone) {
      updates.phone = cartData.customer_phone;
    }
    if (cartData.customer_first_name) {
      updates.firstName = cartData.customer_first_name;
    }
    if (cartData.customer_last_name) {
      updates.lastName = cartData.customer_last_name;
    }
    if (cartData.country) {
      updates.country = cartData.country;
    }
    // Marketing preferences
    if (cartData.accepts_email_marketing !== undefined) {
      updates.emailOffers = cartData.accepts_email_marketing;
    }
    if (cartData.accepts_sms_marketing !== undefined) {
      updates.textOffers = cartData.accepts_sms_marketing;
    }

    // Pre-fill shipping address (only if not stale)
    // Uses addressToFormFields for backward compatibility with legacy formats
    // (handles both address1/zip and address/postal_code formats)
    if (cartData.shipping_address_json && !isStaleCart) {
      const shippingFields = addressToFormFields(cartData.shipping_address_json, 'shipping');
      // Only copy address fields (not firstName/lastName which are already handled above)
      if (shippingFields.shippingAddress) updates.shippingAddress = shippingFields.shippingAddress;
      if (shippingFields.shippingApt) updates.shippingApt = shippingFields.shippingApt;
      if (shippingFields.shippingCity) updates.shippingCity = shippingFields.shippingCity;
      // Note: shippingState is set AFTER provinces load (see loadProvinces useEffect)
      if (shippingFields.shippingZip) updates.shippingZip = shippingFields.shippingZip;
      if (shippingFields.country) {
        let countryValue = shippingFields.country;
        // Check if it's a 2-letter country code and convert to name
        // PayPal Express sends country_code (e.g., "PE") but form expects name (e.g., "Peru")
        if (countryValue && countryValue.length === 2 && availableCountries.length > 0) {
          const matchedCountry = availableCountries.find(c => c.code === countryValue.toUpperCase());
          if (matchedCountry) {
            countryValue = matchedCountry.name;
          }
        }
        updates.country = countryValue;
      }
    }

    // Pre-fill billing address if different from shipping
    // Uses addressToFormFields for backward compatibility with legacy formats
    if (cartData.billing_address_json && !isStaleCart) {
      setBillingSameAsShipping(false);
      const billingFields = addressToFormFields(cartData.billing_address_json, 'billing');
      Object.assign(updates, billingFields);
    }

    // Update form state
    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  };

  /**
   * Validate card fields on submit using Braintree's getState() as source of truth
   * Shows inline errors for each field and focuses on the first error
   * @returns {boolean} true if all card fields are valid
   */
  const validateCardFieldsOnSubmit = () => {
    if (!hostedFieldsInstance) {
      setError("Payment system not ready. Please wait a moment and try again.");
      return false;
    }

    const state = hostedFieldsInstance.getState();
    const fields = state.fields;
    const errors = {};
    let firstErrorField = null;

    // Error messages for empty fields (Shopify style)
    const emptyFieldMessages = {
      number: "Enter a card number",
      expirationDate: "Enter a valid expiration date",
      cvv: "Enter the CVV or security code on your card",
      cardholderName: "Enter the name on card"
    };

    // Error messages for invalid fields
    const invalidFieldMessages = {
      number: "Enter a valid card number",
      expirationDate: "Enter a valid expiration date (MM / YY)",
      cvv: "Enter a valid security code",
      cardholderName: "Enter the name on card"
    };

    // Check each field
    const fieldOrder = ['number', 'expirationDate', 'cvv', 'cardholderName'];

    for (const fieldName of fieldOrder) {
      const field = fields[fieldName];

      if (field.isEmpty) {
        errors[fieldName] = emptyFieldMessages[fieldName];
        if (!firstErrorField) firstErrorField = fieldName;
      } else if (!field.isValid) {
        errors[fieldName] = invalidFieldMessages[fieldName];
        if (!firstErrorField) firstErrorField = fieldName;
      }
    }

    // Update error state
    setCardFieldErrors(errors);

    // If there are errors, scroll and focus on first error field
    if (firstErrorField) {
      // Map field names to DOM element IDs
      const fieldIdMap = {
        number: 'card-number',
        expirationDate: 'expiration-date',
        cvv: 'cvv',
        cardholderName: 'cardholder-name'
      };

      const elementId = fieldIdMap[firstErrorField];
      const element = document.getElementById(elementId);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Focus on the first error field using Braintree's focus method
      try {
        hostedFieldsInstance.focus(firstErrorField);
      } catch (e) {
        logger.warn('Could not focus on field:', e);
      }

      return false;
    }

    return true;
  };

  // Validate form
  const validateForm = () => {
    // Check required fields
    const requiredFields = [
      "email",
      "firstName",
      "lastName",
      "shippingAddress",
      "shippingCity",
      "shippingState",
      "shippingZip",
    ];

    for (let field of requiredFields) {
      if (!formData[field]) {
        setError(
          `Please fill in ${field.replace(/([A-Z])/g, " $1").toLowerCase()}`
        );
        return false;
      }
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Validate billing address if different (only for card payments)
    if (paymentMethod === 'card' && !billingSameAsShipping) {
      const billingFields = [
        "billingFirstName",
        "billingLastName",
        "billingAddress",
        "billingCity",
        "billingState",
        "billingZip",
      ];
      for (let field of billingFields) {
        if (!formData[field]) {
          setError(
            `Please fill in billing ${field
              .replace("billing", "")
              .replace(/([A-Z])/g, " $1")
              .toLowerCase()}`
          );
          return false;
        }
      }
    }

    // Validate card fields (only for card payments) - uses Braintree getState() as source of truth
    if (paymentMethod === 'card') {
      if (!validateCardFieldsOnSubmit()) {
        return false;
      }
    }

    return true;
  };

  /**
   * Validate form for PayPal - shows errors and scrolls to first invalid field
   * Same behavior as credit card validation but without card fields
   */
  const validateFormForPayPal = useCallback(() => {
    // Field mappings for error messages and DOM IDs
    const fieldMappings = {
      email: { label: 'email', inputId: 'email' },
      firstName: { label: 'first name', inputId: 'firstName' },
      lastName: { label: 'last name', inputId: 'lastName' },
      shippingAddress: { label: 'shipping address', inputId: 'shippingAddress' },
      shippingCity: { label: 'city', inputId: 'shippingCity' },
      shippingState: { label: 'state', inputId: 'shippingState' },
      shippingZip: { label: 'ZIP code', inputId: 'shippingZip' },
    };

    // Check required fields
    for (let [field, config] of Object.entries(fieldMappings)) {
      if (!formData[field] || formData[field].trim() === '') {
        setError(`Please fill in ${config.label}`);
        // Scroll to the field
        const element = document.getElementById(config.inputId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
        return false;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      const element = document.getElementById('email');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return false;
    }

    // Check shipping method is selected
    if (!shippingMethod) {
      setError('Please select a shipping method');
      const element = document.querySelector('.shipping-methods');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    setError('');
    return true;
  }, [formData, shippingMethod]);

  /**
   * Calculate total amount for PayPal
   * Uses centralized calculation from utils/cartCalculations.js
   */
  const calculateTotal = useCallback(() => {
    const selectedMethod = shippingMethods.find(m => m.handle === shippingMethod);
    const shippingCost = selectedMethod ? parseFloat(selectedMethod.price) : 0;

    return calculateCartTotalFormatted({
      cart,
      shippingCost,
      tip: appliedTip,
    });
  }, [cart, shippingMethods, shippingMethod, appliedTip]);

  // Render PayPal Express Checkout button when ready
  useEffect(() => {
    let timer;
    if (paypalReady && paypalInstance) {
      timer = setTimeout(() => {
        renderPayPalButton({
          containerId: 'paypal-button-express',
          isExpressCheckout: true,
          isSubmitButton: false,
          calculateTotal,
          onApprove: async (nonce, details, isExpress) => {
            setProcessing(true);
            setError('');
            await processPayPalPayment(nonce, details, isExpress);
          },
          onError: (err) => {
            console.error('PayPal error:', err);
            setError('PayPal encountered an error. Please try again.');
          },
          onCancel: () => {
            logger.debug('PayPal payment cancelled');
          },
        });
      }, 100);
    }
    return () => {
      if (timer) clearTimeout(timer);
      // Cleanup button on unmount or dependency change
      cleanupPayPalButton('paypal-button-express');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalReady, paypalInstance, renderPayPalButton]);

  // Render PayPal submit button when PayPal payment method is selected
  useEffect(() => {
    let timer;
    if (paypalReady && paypalInstance && paymentMethod === 'paypal') {
      timer = setTimeout(() => {
        renderPayPalButton({
          containerId: 'paypal-button-submit',
          isExpressCheckout: false,
          isSubmitButton: true,
          calculateTotal,
          onValidate: validateFormForPayPal,
          onApprove: async (nonce, details, isExpress) => {
            setProcessing(true);
            setError('');
            await processPayPalPayment(nonce, details, isExpress);
          },
          onError: (err) => {
            console.error('PayPal error:', err);
            setError('PayPal encountered an error. Please try again.');
          },
          onCancel: () => {
            logger.debug('PayPal payment cancelled');
          },
        });
      }, 100);
    }
    return () => {
      if (timer) clearTimeout(timer);
      // Cleanup button on unmount or dependency change
      cleanupPayPalButton('paypal-button-submit');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalReady, paypalInstance, paymentMethod, renderPayPalButton]);

  /**
   * Process PayPal payment after tokenization
   */
  const processPayPalPayment = async (nonce, paypalDetails, isExpressCheckout) => {
    try {
      // Use PayPal shipping address if express checkout, otherwise use form data
      const paypalShipping = paypalDetails?.shippingAddress;

      // Use helper functions for state code conversion
      const shippingStateCode = isExpressCheckout && paypalShipping
        ? paypalShipping.state
        : getStateCode(formData.shippingState);
      const countryCode = isExpressCheckout && paypalShipping
        ? paypalShipping.countryCode
        : getCountryCode(formData.country);

      // Build shipping address (using normalized Shopify format)
      const shippingAddress = isExpressCheckout && paypalShipping
        ? normalizePayPalAddress(paypalShipping)
        : formFieldsToAddress(formData, 'shipping', {
            stateCode: shippingStateCode,
            countryCode: countryCode,
          });

      // Build billing address (same as shipping for PayPal)
      const billingAddress = shippingAddress;

      // Process checkout
      const result = await api.processCheckout({
        cart_id: cartId,
        nonce: nonce,
        payment_method: 'paypal',
        customer_data: {
          email: paypalDetails?.email || formData.email,
          first_name: paypalDetails?.firstName || formData.firstName,
          last_name: paypalDetails?.lastName || formData.lastName,
          phone: formData.phone,
          accepts_email_marketing: formData.emailOffers,
          accepts_sms_marketing: formData.textOffers,
          country: formData.country,
        },
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        device_data: dataCollectorInstance?.deviceData,
        shipping_method_id: shippingMethod,
        tip_amount: appliedTip,
      });

      if (result.success) {
        // Build thank you URL with origin and event_id for tracking
        const thankYouParams = new URLSearchParams({
          order_id: result.order_id,
          origin: cart?.store?.domain || 'localhost'
        });
        if (result.event_id) {
          thankYouParams.set('event_id', result.event_id);
        }
        // Preserve GA4 cross-domain _gl parameter if present
        const glParam = searchParams.get('_gl');
        if (glParam) {
          thankYouParams.set('_gl', glParam);
        }
        navigate(`/thank-you?${thankYouParams.toString()}`);
      } else {
        setError(result.error || 'Payment failed. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('PayPal payment processing error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
      setProcessing(false);
    }
  };

  // Handle payment submission (Card only)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setProcessing(true);
    setError("");

    try {
      // 1. Tokenize card with Hosted Fields - include bin for 3DS
      const tokenizeResult = await hostedFieldsInstance.tokenize();
      const { nonce, details } = tokenizeResult;
      const cardBin = details?.bin;  // BIN required for 3DS

      // Use helper functions for state code conversion
      const shippingStateCode = getStateCode(formData.shippingState);
      const billingStateCode = getBillingStateCode(formData.billingState);

      // 2. Prepare billing address (using normalized Shopify format)
      const shippingCountryCode = getCountryCode(formData.country);
      const billingCountryCode = billingSameAsShipping
        ? shippingCountryCode
        : getCountryCode(formData.billingCountry);

      // Build shipping address in normalized format (for reuse)
      const normalizedShippingAddress = formFieldsToAddress(formData, 'shipping', {
        stateCode: shippingStateCode,
        countryCode: shippingCountryCode,
      });

      const billingAddress = billingSameAsShipping
        ? normalizedShippingAddress
        : formFieldsToAddress(formData, 'billing', {
            billingStateCode: billingStateCode,
            billingCountryCode: billingCountryCode,
          });

      // Variables for 3D Secure
      let finalNonce = nonce;
      let threeDSecureInfo = null;

      // 3. 3D Secure verification (if enabled)
      if (threeDSMode !== 'disabled' && threeDSecureReady) {
        try {
          const totalAmount = calculateTotal();

          // Prepare billingAddress with Braintree field names
          const braintreeBillingAddress = {
            givenName: billingSameAsShipping ? formData.firstName : formData.billingFirstName,
            surname: billingSameAsShipping ? formData.lastName : formData.billingLastName,
            streetAddress: billingSameAsShipping ? formData.shippingAddress : formData.billingAddress,
            extendedAddress: billingSameAsShipping ? formData.shippingApt : formData.billingApt,
            locality: billingSameAsShipping ? formData.shippingCity : formData.billingCity,
            region: billingSameAsShipping ? shippingStateCode : billingStateCode,
            postalCode: billingSameAsShipping ? formData.shippingZip : formData.billingZip,
            countryCodeAlpha2: billingCountryCode,
          };

          const verifyResult = await verifyCard({
            nonce,
            bin: cardBin,
            amount: totalAmount,
            email: formData.email,
            billingAddress: braintreeBillingAddress,
          });

          if (verifyResult.skipped) {
            // 3DS was not required for this country
            logger.debug('3DS skipped:', verifyResult.reason);
            finalNonce = nonce;
          } else {
            // 3DS was performed
            finalNonce = verifyResult.nonce;
            threeDSecureInfo = {
              status: verifyResult.threeDSecureInfo.status,
              liability_shifted: verifyResult.threeDSecureInfo.liabilityShifted,
              version: verifyResult.threeDSecureInfo.version,
            };
            logger.debug('✅ 3DS verified:', threeDSecureInfo);
          }
        } catch (threeDSError) {
          // 3DS specific error
          console.error('3DS Error:', threeDSError);
          setError(threeDSError.userMessage || 'Card verification failed. Please try again.');
          setProcessing(false);
          return;
        }
      }

      // 4. Revalidate shipping methods before payment (fresh check from Shopify)
      // This prevents stale handles from causing errors (AFTER 3DS to avoid modal interruption)
      logger.debug("🔍 Revalidating shipping methods before payment...");
      try {
        const countryCode = getCountryCode(formData.country);
        const stateCode = getStateCode(formData.shippingState);

        // Get fresh shipping methods from Shopify
        const freshResult = await api.getShippingMethods(cartId, {
          address1: formData.shippingAddress,
          address2: formData.shippingApt,
          city: formData.shippingCity,
          provinceCode: stateCode,
          zip: formData.shippingZip,
          countryCode: countryCode,
          firstName: formData.firstName,
          lastName: formData.lastName,
        });

        logger.debug("Fresh shipping methods:", freshResult.shipping_methods);

        if (
          !freshResult.shipping_methods ||
          freshResult.shipping_methods.length === 0
        ) {
          setError("No shipping methods available for this address.");
          setProcessing(false);
          return;
        }

        // Validate selected handle against fresh list
        const isValidHandle = freshResult.shipping_methods.some(
          (m) => m.handle === shippingMethod
        );
        logger.debug("Selected handle:", shippingMethod);
        logger.debug("Is valid?", isValidHandle);

        if (!isValidHandle) {
          // Handle is stale - update to first valid method
          logger.warn(
            "⚠️ Shipping method handle is stale, updating to first available"
          );
          const newHandle = freshResult.shipping_methods[0].handle;
          setShippingMethod(newHandle);
          setShippingMethods(freshResult.shipping_methods);
          setError(
            "Shipping method was updated. Please review and click Pay again."
          );
          setProcessing(false);
          return;
        }

        logger.debug("✅ Shipping method validated successfully");
      } catch (err) {
        console.error("❌ Error revalidating shipping methods:", err);
        setError("Unable to validate shipping method. Please try again.");
        setProcessing(false);
        return;
      }

      // 5. Send to backend (using normalized Shopify format for addresses)
      const result = await api.processCheckout({
        cart_id: cartId,
        nonce: finalNonce,  // Use verified nonce if 3DS was performed
        three_d_secure_info: threeDSecureInfo,  // 3DS verification result (null if skipped)
        customer_data: {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          accepts_email_marketing: formData.emailOffers,
          accepts_sms_marketing: formData.textOffers,
          country: formData.country,
        },
        shipping_address: normalizedShippingAddress,
        billing_address: billingAddress,
        device_data: dataCollectorInstance
          ? dataCollectorInstance.deviceData
          : null,
        shipping_method_id: shippingMethod, // Send Shopify handle directly (string)
        tip_amount: appliedTip, // Send tip amount to backend
      });

      if (result.success) {
        // Build thank you URL with origin and event_id for tracking
        const thankYouParams = new URLSearchParams({
          order_id: result.order_id,
          origin: cart?.store?.domain || 'localhost'
        });
        // Add event_id if returned from backend (for CAPI deduplication)
        if (result.event_id) {
          thankYouParams.set('event_id', result.event_id);
        }
        // Preserve GA4 cross-domain _gl parameter if present
        const glParam = searchParams.get('_gl');
        if (glParam) {
          thankYouParams.set('_gl', glParam);
        }
        navigate(`/thank-you?${thankYouParams.toString()}`);
      } else {
        setError(result.error || "Payment failed. Please try again.");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "Payment processing failed. Please try again.");
      setProcessing(false);
    }
  };

  /**
   * Google Places Callbacks (AutocompleteService API)
   * Handlers for the AddressAutocomplete component
   *
   * ⚠️ IMPORTANT: Memoized with useCallback to prevent unnecessary re-renders
   * These functions are passed to AddressAutocomplete component as props
   *
   * ⚠️ CRITICAL: Must be defined BEFORE any conditional returns (loading, error)
   * to satisfy React Hooks Rules (hooks must be called in same order every render)
   */
  const handleGooglePlaceSelected = useCallback(
    ({ parsedAddress, streetAddress }) => {

      const updates = { shippingAddress: streetAddress || "" };
      if (parsedAddress.city) updates.shippingCity = parsedAddress.city;
      if (parsedAddress.state) updates.shippingState = parsedAddress.state;
      if (parsedAddress.zip) updates.shippingZip = parsedAddress.zip;

      setFormData((prev) => ({ ...prev, ...updates }));
      setAddressSource("google");
      setGooglePlaceSelected(true);

    },
    []
  ); // Empty deps - uses functional setState to avoid dependencies

  const handleGoogleAddressAutofilled = useCallback(
    ({ parsedAddress, streetAddress }) => {
      // Save immediately with actual data from Google Places
      saveFormDataIncremental(parsedAddress, streetAddress);
      setShouldReloadShipping(true);
    },
    [saveFormDataIncremental]
  );

  /**
   * 🔥 FIX: Enable Google Places on Address field focus (cart recovery)
   * Prevents dropdown flash during cart recovery pre-fill by only enabling
   * Google Places when user actively interacts with the field.
   */
  const handleAddressFieldFocus = useCallback(() => {
    if (!googlePlacesEnabled) {
      setGooglePlacesEnabled(true);
    }
  }, [googlePlacesEnabled]);

  // Show skeleton loading while verifying cart status (prevents flash)
  if (loading) {
    return <CheckoutSkeleton showHeader={true} />;
  }

  // Only show full loading screen if there's a critical error
  if (error && !cart && cartLoading) {
    return (
      <div className="checkout-container">
        <div className="error-page">
          <h1>Error</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Tip calculation helpers
  const subtotal = cart?.volume_discount_total
    ? parseFloat(cart.volume_discount_total)
    : parseFloat(cart?.subtotal || 0);

  const tipAmounts = {
    5: (subtotal * 0.05).toFixed(2),
    10: (subtotal * 0.1).toFixed(2),
    20: (subtotal * 0.2).toFixed(2),
  };

  const handleTipOptionClick = (option) => {
    setSelectedTipOption(option);

    // Aplicar tip inmediatamente para opciones preset
    if (option === "none") {
      setAppliedTip(0);
      setShowTipThankYou(false);
    } else if (option !== "custom") {
      // Para opciones 5%, 10%, 20% - aplicar inmediatamente
      const amount = parseFloat(tipAmounts[option]) || 0;
      setAppliedTip(amount);
      setShowTipThankYou(amount > 0);
    }
    // Si es 'custom', solo seleccionar sin aplicar (esperar a "Add tip" o spinners)
  };

  const handleApplyTip = async () => {
    setTipProcessing(true);

    // Simular un pequeño delay para mostrar el loading
    await new Promise((resolve) => setTimeout(resolve, 300));

    if (selectedTipOption === "none") {
      setAppliedTip(0);
      setShowTipThankYou(false);
      setLastAppliedCustomTip("");
      setTipError("");
    } else if (selectedTipOption === "custom") {
      const amount = parseFloat(customTipAmount) || 0;

      // Validar que el tip no exceda el subtotal
      if (amount > subtotal) {
        setTipError(`Tip cannot exceed order amount ($${subtotal.toFixed(2)})`);
        setTipProcessing(false);
        return;
      }

      setAppliedTip(amount);
      setShowTipThankYou(amount > 0);
      setLastAppliedCustomTip(customTipAmount); // Guardar el valor aplicado
      setTipError("");
    } else {
      const amount = parseFloat(tipAmounts[selectedTipOption]) || 0;
      setAppliedTip(amount);
      setShowTipThankYou(amount > 0);
      setLastAppliedCustomTip("");
      setTipError("");
    }

    setTipProcessing(false);
  };

  const incrementCustomTip = () => {
    const current = parseFloat(customTipAmount) || 0;
    const newAmount = parseFloat((current + 1).toFixed(2));

    // Validar que no exceda el subtotal
    if (newAmount > subtotal) {
      setTipError(`Tip cannot exceed order amount ($${subtotal.toFixed(2)})`);
      return;
    }

    setCustomTipAmount(newAmount.toFixed(2));

    // Aplicar tip inmediatamente y seleccionar opción custom
    setSelectedTipOption("custom");
    setAppliedTip(newAmount);
    setShowTipThankYou(newAmount > 0);
    setTipError("");
  };

  const decrementCustomTip = () => {
    const current = parseFloat(customTipAmount) || 0;
    if (current > 0) {
      const newAmount = parseFloat((current - 1).toFixed(2));
      setCustomTipAmount(newAmount.toFixed(2));

      // Aplicar tip inmediatamente y seleccionar opción custom
      setSelectedTipOption("custom");
      setAppliedTip(newAmount);
      setShowTipThankYou(newAmount > 0);
      setTipError("");
    }
  };

  // Payment Card Icons Component (Simple & Professional)
  const PaymentCardIcons = () => (
    <div className="payment-card-icons">
      <img
        src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1/assets/visa.sxIq5Dot.svg"
        alt="Visa"
        width="38"
        height="24"
      />
      <img
        src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1/assets/mastercard.1c4_lyMp.svg"
        alt="Mastercard"
        width="38"
        height="24"
      />
      <img
        src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1/assets/amex.Csr7hRoy.svg"
        alt="American Express"
        width="38"
        height="24"
      />
    </div>
  );

  return (
    <div className="checkout-container">
      {/* Header (Shopify Style) */}
      <div
        className="checkout-header"
        style={{
          backgroundColor:
            config?.branding?.header_background_color || "#2c3e50",
        }}>
        <div
          style={{
            display: "flex",
            justifyContent:
              config?.branding?.logo_alignment === "left"
                ? "flex-start"
                : "center",
            alignItems: "center",
            maxWidth:
              config?.branding?.logo_alignment === "left" ? "1200px" : "100%",
            width: "100%",
            margin:
              config?.branding?.logo_alignment === "left" ? "0 auto" : "0",
            position: "relative",
          }}>
          <div className="logo">
            {config?.branding?.logo_url ? (
              <img
                src={config.branding.logo_url}
                alt={config.name}
                style={{
                  maxHeight: `${config.branding.logo_max_height || 40}px`,
                }}
              />
            ) : (
              <h1>{config?.name || "Checkout"}</h1>
            )}
          </div>
        </div>
      </div>

      {isLoadingCart ? (
        <CheckoutSkeleton showHeader={false} />
      ) : (
        <div className="checkout-content">
          {/* Left Column - Form */}
          <div className="checkout-main">
            {error && <div className="error-message">⚠️ {error}</div>}

            <form onSubmit={handleSubmit}>
              {/* PayPal Express Mode Banner - Show when coming from landing page */}
              {isPayPalExpressMode && (
                <div className="paypal-express-banner">
                  <img
                    src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png"
                    alt="PayPal"
                    height="20"
                  />
                  <span>Complete your order with PayPal</span>
                </div>
              )}

              {/* Express Checkout - PayPal (hidden in PayPal Express mode) */}
              {paypalReady && !isPayPalExpressMode && (
                <section className="express-checkout">
                  <p className="express-checkout-label">Express checkout</p>
                  <div id="paypal-button-express" className="paypal-button-container"></div>
                  <div className="express-checkout-divider">
                    <span>OR</span>
                  </div>
                </section>
              )}

              {/* Contact Information */}
              <section className="form-section">
                <h2>Contact</h2>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    disabled={processing}
                    required
                  />
                </div>
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="emailOffers"
                      checked={formData.emailOffers}
                      onChange={handleMarketingPreferenceChange}
                      disabled={processing}
                    />
                    Email me with news and offers
                  </label>
                </div>
              </section>

              {/* Delivery */}
              <section className="form-section">
                <h2>Delivery</h2>

                <div className="form-group">
                  <label htmlFor="country">Country / Region</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    disabled={processing}>
                    {countries.length > 0 ? (
                      countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))
                    ) : (
                      <option value="United States">United States</option>
                    )}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First name</label>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleShippingAddressBlur}
                      disabled={processing}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last name</label>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleShippingAddressBlur}
                      disabled={processing}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="shippingAddress">Address</label>
                  {/* Google Places Autocomplete (AutocompleteService - Programmatic API) */}
                  <AddressAutocomplete
                    id="shippingAddress"
                    name="shippingAddress"
                    value={formData.shippingAddress}
                    onChange={(e, value) => {
                      // Call both handlers: AddressAutocomplete internal handler and Checkout handler
                      handleInputChange(e);
                    }}
                    onFocus={handleAddressFieldFocus}
                    onPlaceSelected={handleGooglePlaceSelected}
                    onAddressAutofilled={handleGoogleAddressAutofilled}
                    placeholder="Address"
                    required={true}
                    enabled={!processing && googlePlacesEnabled}
                    enableAnalytics={true}
                    apartmentFieldId="shippingApt"
                    debounceMs={300}
                    countryCode={getCountryCode(formData.country)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="shippingApt">
                    Apartment, suite, etc. (optional)
                  </label>
                  <input
                    id="shippingApt"
                    type="text"
                    name="shippingApt"
                    placeholder="Apartment, suite, etc. (optional)"
                    value={formData.shippingApt}
                    onChange={(e) => {
                      handleInputChange(e);
                      debouncedSave(); // Auto-save after 1.5s of inactivity
                    }}
                    disabled={processing}
                  />
                </div>

                <div className="form-row-3">
                  <div className="form-group">
                    <label htmlFor="shippingCity">City</label>
                    <input
                      id="shippingCity"
                      type="text"
                      name="shippingCity"
                      placeholder="City"
                      value={formData.shippingCity}
                      onChange={handleInputChange}
                      onBlur={handleShippingAddressBlur}
                      disabled={processing}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="shippingState">State</label>
                    <select
                      id="shippingState"
                      name="shippingState"
                      value={formData.shippingState}
                      onChange={handleInputChange}
                      onBlur={handleShippingAddressBlur}
                      disabled={processing}
                      required>
                      <option value="">State</option>
                      {(provinces.length > 0 ? provinces : US_STATES).map(
                        (province) => (
                          <option key={province.code} value={province.name}>
                            {province.name}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="shippingZip">ZIP code</label>
                    <input
                      id="shippingZip"
                      type="text"
                      name="shippingZip"
                      placeholder="ZIP code"
                      value={formData.shippingZip}
                      onChange={handleInputChange}
                      onBlur={handleShippingAddressBlur}
                      disabled={processing}
                      required
                    />
                  </div>
                </div>

                {/* Phone - Siempre visible con selector de país (estilo Shopify) */}
                <div className={`form-group phone-input-group ${formData.phone ? 'has-value' : ''} ${phoneError ? 'has-error' : ''}`}>
                  <label htmlFor="phone">Phone</label>
                  <div className="phone-input-wrapper">
                    <PhoneInput
                      id="phone"
                      international
                      labels={countryLabelsWithCodes}
                      defaultCountry={getCountryCode(formData.country)}
                      value={formData.phone}
                      onChange={(value) => setFormData({...formData, phone: value || ''})}
                      onBlur={handlePhoneBlur}
                      disabled={processing}
                      placeholder="Phone number"
                    />
                    <span className="phone-help-icon" data-tooltip="In case we need to contact you about your order" tabIndex="0">?</span>
                  </div>
                  {phoneError && <span className="field-error">Enter a valid phone number</span>}
                </div>

                {/* SMS Marketing Checkbox */}
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="textOffers"
                      checked={formData.textOffers}
                      onChange={handleMarketingPreferenceChange}
                      disabled={processing}
                    />
                    Text me with news and offers
                  </label>
                </div>

                {/* Mobile Phone - Solo con checkbox, auto-fill del Phone (estilo Shopify) */}
                {formData.textOffers && (
                  <div className={`form-group phone-input-group has-mobile-icon ${formData.mobilePhone ? 'has-value' : ''} ${mobilePhoneError ? 'has-error' : ''}`}>
                    <label htmlFor="mobilePhone">Mobile phone number</label>
                    <div className="phone-input-wrapper">
                      {/* Icono de celular SVG (estilo Shopify) */}
                      <span className="mobile-phone-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" focusable="false" aria-hidden="true">
                          <path d="M3.25 3.25c0-.966.784-1.75 1.75-1.75h4c.966 0 1.75.784 1.75 1.75v7.5A1.75 1.75 0 0 1 9 12.5H5a1.75 1.75 0 0 1-1.75-1.75z"></path>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 10.5h2"></path>
                          <path strokeLinecap="round" d="M6 2h2"></path>
                        </svg>
                      </span>
                      <PhoneInput
                        id="mobilePhone"
                        international
                        labels={countryLabelsWithCodes}
                        defaultCountry={getCountryCode(formData.country)}
                        value={formData.mobilePhone || formData.phone}
                        onChange={(value) => setFormData({...formData, mobilePhone: value || ''})}
                        onBlur={handleMobilePhoneBlur}
                        disabled={processing}
                        placeholder="Mobile phone number"
                      />
                    </div>
                    {mobilePhoneError && <span className="field-error">Enter a valid phone number</span>}
                    <p className="field-hint">
                      By signing up via text, you agree to receive recurring automated marketing messages,
                      including cart reminders, at the phone number provided. Consent is not a condition of
                      purchase. Reply STOP to unsubscribe. Reply HELP for help. Message frequency varies. Msg
                      & data rates may apply.
                    </p>
                  </div>
                )}
              </section>

              {/* Shipping Method */}
              <section className="form-section">
                <h2>Shipping method</h2>

                {!formData.shippingCity ||
                !formData.shippingState ||
                !formData.shippingZip ? (
                  <div
                    style={{
                      padding: "20px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      textAlign: "center",
                    }}>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#6b7280",
                        margin: 0,
                      }}>
                      Enter your shipping address to see available methods.
                    </p>
                  </div>
                ) : loadingShippingMethods ? (
                  <ShippingMethodSkeleton />
                ) : shippingMethods.length > 0 ? (
                  <>
                    <div className="shipping-options">
                      {shippingMethods.map((method, index) => (
                        <label
                          key={method.id || method.handle || index}
                          className="shipping-option">
                          <input
                            type="radio"
                            name="shippingMethod"
                            value={method.handle}
                            checked={shippingMethod === method.handle}
                            onChange={(e) => setShippingMethod(e.target.value)}
                            disabled={processing}
                          />
                          <div className="shipping-option-info">
                            <div className="title">{method.title}</div>
                            {method.description && (
                              <div className="subtitle">
                                {method.description}
                              </div>
                            )}
                          </div>
                          <div className="shipping-option-price">
                            ${parseFloat(method.price).toFixed(2)}
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                ) : shouldReloadShipping ? (
                  <div
                    style={{
                      padding: "20px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      textAlign: "center",
                    }}>
                    <div
                      className="spinner-small"
                      style={{ margin: "0 auto 12px" }}></div>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#6b7280",
                        margin: 0,
                      }}>
                      Calculando métodos de envío disponibles...
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "20px",
                      background: "#fef3c7",
                      borderRadius: "8px",
                      border: "1px solid #fbbf24",
                      textAlign: "center",
                    }}>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#92400e",
                        margin: 0,
                      }}>
                      No shipping methods available for this address.
                    </p>
                  </div>
                )}
              </section>

              {/* Payment Method */}
              <section className="form-section payment-section">
                <h2>Payment</h2>
                <p className="payment-secure-text">All transactions are secure and encrypted.</p>

                {braintreeLoading ? (
                  <div className="payment-loading">
                    <div className="spinner-small"></div>
                    <p>Initializing secure payment...</p>
                  </div>
                ) : (
                  <div className="payment-methods">
                    {/* Credit Card Option */}
                    <div
                      className={`payment-method-option ${paymentMethod === 'card' ? 'active' : ''}`}
                      onClick={() => setPaymentMethod('card')}
                    >
                      <div className="payment-method-header">
                        <label className="payment-method-radio">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="card"
                            checked={paymentMethod === 'card'}
                            onChange={() => setPaymentMethod('card')}
                          />
                          <span className="radio-custom"></span>
                          <span className="payment-method-label">Credit card</span>
                        </label>
                        <PaymentCardIcons />
                      </div>

                      {/* Card Fields - Only visible when card is selected */}
                      {paymentMethod === 'card' && (
                        <div className="payment-method-content">
                          {/* Card Number */}
                          <div className="form-group">
                            <div id="card-number" className="hosted-field"></div>
                            {cardFieldErrors.number && (
                              <div className="field-error">{cardFieldErrors.number}</div>
                            )}
                          </div>

                          {/* Expiration Date & CVV */}
                          <div className="form-row">
                            <div className="form-group">
                              <div id="expiration-date" className="hosted-field"></div>
                              {cardFieldErrors.expirationDate && (
                                <div className="field-error">{cardFieldErrors.expirationDate}</div>
                              )}
                            </div>
                            <div className="form-group">
                              <div id="cvv" className="hosted-field"></div>
                              {cardFieldErrors.cvv && (
                                <div className="field-error">{cardFieldErrors.cvv}</div>
                              )}
                            </div>
                          </div>

                          {/* Cardholder Name */}
                          <div className="form-group">
                            <div id="cardholder-name" className="hosted-field"></div>
                            {cardFieldErrors.cardholderName && (
                              <div className="field-error">{cardFieldErrors.cardholderName}</div>
                            )}
                          </div>

                          {/* Billing Checkbox inside card section */}
                          <div className="billing-checkbox">
                            <label>
                              <input
                                type="checkbox"
                                checked={billingSameAsShipping}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setBillingSameAsShipping(checked);

                                  // Al desmarcar: inicializar billing con valores de shipping
                                  if (!checked) {
                                    setFormData((prev) => ({
                                      ...prev,
                                      billingCountry: prev.billingCountry || prev.country,
                                      billingState: "",  // Reset para que cargue nuevas provincias
                                    }));
                                  }
                                }}
                                disabled={processing}
                              />
                              <span>Use shipping address as billing address</span>
                            </label>
                          </div>

                          {/* Billing Address - Inside Credit Card (Shopify style) */}
                          {!billingSameAsShipping && (
                            <div className="billing-address-form">
                              <h3>Billing address</h3>

                              {/* Country Selector - First (like shipping) */}
                              <div className="form-group">
                                <label htmlFor="billingCountry">Country / Region</label>
                                <select
                                  id="billingCountry"
                                  name="billingCountry"
                                  value={formData.billingCountry}
                                  onChange={(e) => {
                                    // Reset billingState when country changes
                                    setFormData((prev) => ({
                                      ...prev,
                                      billingCountry: e.target.value,
                                      billingState: "",
                                    }));
                                  }}
                                  disabled={processing}
                                >
                                  {countries.length > 0 ? (
                                    countries.map((country) => (
                                      <option key={country.code} value={country.name}>
                                        {country.name}
                                      </option>
                                    ))
                                  ) : (
                                    <option value="United States">United States</option>
                                  )}
                                </select>
                              </div>

                              {/* First name / Last name row */}
                              <div className="form-row">
                                <div className="form-group">
                                  <input
                                    type="text"
                                    name="billingFirstName"
                                    placeholder="First name"
                                    value={formData.billingFirstName}
                                    onChange={handleInputChange}
                                    disabled={processing}
                                  />
                                </div>
                                <div className="form-group">
                                  <input
                                    type="text"
                                    name="billingLastName"
                                    placeholder="Last name"
                                    value={formData.billingLastName}
                                    onChange={handleInputChange}
                                    disabled={processing}
                                  />
                                </div>
                              </div>

                              {/* Address */}
                              <div className="form-group">
                                <input
                                  type="text"
                                  name="billingAddress"
                                  placeholder="Address"
                                  value={formData.billingAddress}
                                  onChange={handleInputChange}
                                  disabled={processing}
                                />
                              </div>

                              {/* Apartment (optional) */}
                              <div className="form-group">
                                <input
                                  type="text"
                                  name="billingApt"
                                  placeholder="Apartment, suite, etc. (optional)"
                                  value={formData.billingApt}
                                  onChange={handleInputChange}
                                  disabled={processing}
                                />
                              </div>

                              {/* City / State / ZIP row (3 columns like shipping) */}
                              <div className="form-row form-row-3">
                                <div className="form-group">
                                  <input
                                    type="text"
                                    name="billingCity"
                                    placeholder="City"
                                    value={formData.billingCity}
                                    onChange={handleInputChange}
                                    disabled={processing}
                                  />
                                </div>
                                <div className="form-group">
                                  {billingProvinces.length > 0 ? (
                                    <select
                                      name="billingState"
                                      value={formData.billingState}
                                      onChange={handleInputChange}
                                      disabled={processing}
                                    >
                                      <option value="">State</option>
                                      {billingProvinces.map((province) => (
                                        <option key={province.code} value={province.name}>
                                          {province.name}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      name="billingState"
                                      placeholder="State"
                                      value={formData.billingState}
                                      onChange={handleInputChange}
                                      disabled={processing}
                                    />
                                  )}
                                </div>
                                <div className="form-group">
                                  <input
                                    type="text"
                                    name="billingZip"
                                    placeholder="ZIP code"
                                    value={formData.billingZip}
                                    onChange={handleInputChange}
                                    disabled={processing}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* PayPal Option */}
                    {paypalReady && (
                      <div
                        className={`payment-method-option ${paymentMethod === 'paypal' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('paypal')}
                      >
                        <div className="payment-method-header">
                          <label className="payment-method-radio">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="paypal"
                              checked={paymentMethod === 'paypal'}
                              onChange={() => setPaymentMethod('paypal')}
                            />
                            <span className="radio-custom"></span>
                            <span className="payment-method-label">PayPal</span>
                          </label>
                          <img
                            src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png"
                            alt="PayPal"
                            height="22"
                            className="paypal-logo"
                          />
                        </div>

                        {/* PayPal Info - Only visible when PayPal is selected */}
                        {paymentMethod === 'paypal' && (
                          <div className="payment-method-content paypal-content">
                            {/* PayPal redirect illustration - Shopify style */}
                            <div className="paypal-redirect-illustration">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="-252.3 356.1 163 80.9" className="paypal-redirect-svg">
                                <path fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="2" d="M-108.9 404.1v30c0 1.1-.9 2-2 2H-231c-1.1 0-2-.9-2-2v-75c0-1.1.9-2 2-2h120.1c1.1 0 2 .9 2 2v37m-124.1-29h124.1"></path>
                                <circle cx="-227.8" cy="361.9" r="1.8" fill="currentColor"></circle>
                                <circle cx="-222.2" cy="361.9" r="1.8" fill="currentColor"></circle>
                                <circle cx="-216.6" cy="361.9" r="1.8" fill="currentColor"></circle>
                                <path fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="2" d="M-128.7 400.1H-92m-3.6-4.1 4 4.1-4 4.1"></path>
                              </svg>
                            </div>
                            <p className="paypal-info-text">
                              After clicking "Pay with PayPal", you will be redirected to PayPal to complete your purchase securely.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Tip Section - Collapsible (Shopify Style) */}
              {config?.name && (
                <section className="tip-section">
                  <h2>Add tip</h2>

                  <div className="tip-collapsible">
                    <div className="tip-checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={tipEnabled}
                          onChange={(e) => {
                            setTipEnabled(e.target.checked);
                            if (!e.target.checked) {
                              setAppliedTip(0);
                              setShowTipThankYou(false);
                              setSelectedTipOption("none");
                            }
                          }}
                        />
                        Show your support for the {config.name || "store"} team
                      </label>
                    </div>

                    {tipEnabled && (
                      <div className="tip-content">
                        <div className="tip-options">
                          <button
                            type="button"
                            className={`tip-button ${
                              selectedTipOption === "5" ? "active" : ""
                            }`}
                            onClick={() => handleTipOptionClick("5")}>
                            <div className="tip-button-percentage">5%</div>
                            <div className="tip-button-amount">
                              ${tipAmounts[5]}
                            </div>
                          </button>
                          <button
                            type="button"
                            className={`tip-button ${
                              selectedTipOption === "10" ? "active" : ""
                            }`}
                            onClick={() => handleTipOptionClick("10")}>
                            <div className="tip-button-percentage">10%</div>
                            <div className="tip-button-amount">
                              ${tipAmounts[10]}
                            </div>
                          </button>
                          <button
                            type="button"
                            className={`tip-button ${
                              selectedTipOption === "20" ? "active" : ""
                            }`}
                            onClick={() => handleTipOptionClick("20")}>
                            <div className="tip-button-percentage">20%</div>
                            <div className="tip-button-amount">
                              ${tipAmounts[20]}
                            </div>
                          </button>
                          <button
                            type="button"
                            className={`tip-button ${
                              selectedTipOption === "none" ? "active" : ""
                            }`}
                            onClick={() => handleTipOptionClick("none")}>
                            <div className="tip-button-percentage">None</div>
                          </button>
                        </div>

                        <div
                          className="custom-tip-row"
                          style={{
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: "12px",
                          }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                            }}>
                            <div className="custom-tip-input-wrapper">
                              <label
                                htmlFor="customTip"
                                className="custom-tip-label">
                                Custom tip
                              </label>
                              <div className="custom-tip-input-group">
                                <div className="custom-tip-input-field">
                                  <span className="currency-symbol">$</span>
                                  <input
                                    id="customTip"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={subtotal}
                                    placeholder="Custom tip"
                                    value={customTipAmount}
                                    onChange={(e) => {
                                      setCustomTipAmount(e.target.value);
                                      setSelectedTipOption("custom");
                                      setTipError(""); // Limpiar error al escribir
                                    }}
                                    className="custom-tip-input"
                                  />
                                </div>
                                <div className="custom-tip-spinner">
                                  <button
                                    type="button"
                                    onClick={decrementCustomTip}
                                    className="spinner-button">
                                    −
                                  </button>
                                  <button
                                    type="button"
                                    onClick={incrementCustomTip}
                                    className="spinner-button">
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="tip-submit"
                              onClick={handleApplyTip}
                              disabled={
                                tipProcessing ||
                                selectedTipOption !== "custom" ||
                                !customTipAmount ||
                                customTipAmount === lastAppliedCustomTip
                              }
                              style={{
                                opacity: tipProcessing ? 0.8 : 1,
                                transition: "opacity 0.3s ease",
                              }}>
                              {tipProcessing ? (
                                <>
                                  <span className="spinner-small"></span>
                                  <span
                                    style={{
                                      opacity: 1,
                                      transition: "opacity 0.3s ease",
                                    }}>
                                    Processing...
                                  </span>
                                </>
                              ) : (
                                <span
                                  style={{
                                    opacity: 1,
                                    transition: "opacity 0.3s ease",
                                  }}>
                                  {appliedTip > 0 ? "Update tip" : "Add tip"}
                                </span>
                              )}
                            </button>
                          </div>

                          {tipError && (
                            <p
                              style={{
                                fontSize: "13px",
                                color: "#d82c0d",
                                margin: "0",
                              }}>
                              {tipError}
                            </p>
                          )}

                          {appliedTip > 0 && !tipError && (
                            <p
                              style={{
                                fontSize: "14px",
                                color: "#6d7175",
                                margin: "0",
                              }}>
                              Thank you, we appreciate it.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Submit Button - Only for Card payments */}
              {paymentMethod === 'card' && (
                <button
                  type="submit"
                  className="submit-button"
                  disabled={processing || braintreeLoading}>
                  {processing ? (
                    <>
                      <span className="spinner-small"></span>
                      Processing...
                    </>
                  ) : braintreeLoading ? (
                    <>
                      <span className="spinner-small"></span>
                      Loading payment...
                    </>
                  ) : (
                    "Pay now"
                  )}
                </button>
              )}

              {/* PayPal Button - When PayPal is selected (replaces Pay Now button) */}
              {paymentMethod === 'paypal' && paypalReady && (
                <div className="paypal-submit-container">
                  <div id="paypal-button-submit" className="paypal-button-submit"></div>
                </div>
              )}

              {/* Footer with Policy Links - Dynamic URLs from store domain */}
              <footer className="checkout-footer">
                <div className="footer-links">
                  <a
                    href={`https://${cart?.store?.domain}/policies/refund-policy`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Refund policy
                  </a>
                  <a
                    href={`https://${cart?.store?.domain}/policies/shipping-policy`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Shipping
                  </a>
                  <a
                    href={`https://${cart?.store?.domain}/policies/privacy-policy`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Privacy policy
                  </a>
                  <a
                    href={`https://${cart?.store?.domain}/policies/terms-of-service`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Terms of service
                  </a>
                  <a href={`https://${cart?.store?.domain}/pages/contact`} target="_blank" rel="noopener noreferrer">
                    Contact
                  </a>
                </div>
              </footer>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="checkout-sidebar">
            <OrderSummary
              cart={cart}
              config={config}
              shippingMethods={shippingMethods}
              selectedShippingMethod={shippingMethod}
              loading={cartLoading}
              appliedTip={appliedTip}
              onCartUpdate={refreshCart}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
